import { useEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useDocumentState } from '@/state'
import { LETTER_PAGE_HEIGHT } from '../../DocumentTemplate'
import { VirtualItem } from '@tanstack/react-virtual'

type Args = {
  operationPageId: string
  subPageIds: string[]
  debounceMs?: number
}

export function useActiveSubpageFromVirtualizer({
  operationPageId,
  subPageIds,
  debounceMs = 0,
}: Args) {
  const {
    virtualizer,
    idToIndex,
    currentPageInView,
    setCurrentSubpageInView,
    zoom,
  } = useDocumentState(
    useShallow((s) => ({
      virtualizer: s.virtualizer,
      idToIndex: s.idToIndex,
      currentPageInView: s.currentPageInView,
      setCurrentSubpageInView: s.setCurrentSubpageInView,
      zoom: s.zoom,
    })),
  )

  const rafRef = useRef<number | null>(null)
  const tRef = useRef<number | null>(null)

  useEffect(() => {
    if (!virtualizer) return
    const scrollEl = virtualizer.scrollElement
    if (!(scrollEl instanceof HTMLElement)) return

    const opIdx = idToIndex.get(operationPageId)
    if (opIdx == null) return

    if (currentPageInView !== operationPageId) return

    const compute = () => {
      const items: VirtualItem[] = virtualizer.getVirtualItems()
      const opItem = items.find((it) => it.index === opIdx)
      if (!opItem) return

      const top = scrollEl.scrollTop
      const bottom = top + scrollEl.clientHeight

      const zoomFactor = Math.max(0.0001, (zoom || 100) / 100)
      const subHeight = LETTER_PAGE_HEIGHT * zoomFactor

      let bestIdx = 0
      let bestOverlap = -1

      for (let i = 0; i < Math.max(1, subPageIds.length); i++) {
        const start = opItem.start + i * subHeight
        const end = start + subHeight
        const overlap = Math.max(
          0,
          Math.min(end, bottom) - Math.max(start, top),
        )
        if (overlap > bestOverlap) {
          bestOverlap = overlap
          bestIdx = i
        }
      }

      const id = subPageIds[bestIdx] ?? subPageIds[0]
      if (id) setCurrentSubpageInView(id)
    }

    const schedule = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      const run = () => compute()
      if (debounceMs > 0) {
        if (tRef.current) window.clearTimeout(tRef.current)
        tRef.current = window.setTimeout(run, debounceMs)
      } else {
        rafRef.current = requestAnimationFrame(run)
      }
    }

    const onScroll = () => schedule()

    scrollEl.addEventListener('scroll', onScroll, { passive: true })
    schedule()

    return () => {
      scrollEl.removeEventListener('scroll', onScroll)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      if (tRef.current) window.clearTimeout(tRef.current)
    }
  }, [
    virtualizer,
    idToIndex,
    operationPageId,
    subPageIds,
    debounceMs,
    currentPageInView,
    zoom,
  ])
}
